import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, ChevronDown } from "lucide-react";
import { DISCLAIMER_FULL } from "@/lib/disclaimers";
import { cn } from "@/lib/utils";

type Props = {
  variant?: "banner" | "inline" | "badge" | "footer";
  collapsible?: boolean;
  defaultOpen?: boolean;
  className?: string;
};

export function AcademicDisclaimer({
  variant = "banner",
  collapsible = false,
  defaultOpen = false,
  className,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);

  if (variant === "badge") {
    return (
      <Link
        to="/terms"
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs text-muted-foreground transition hover:border-primary/40 hover:text-foreground",
          className,
        )}
      >
        <ShieldCheck className="h-3.5 w-3.5 text-primary" />
        Assistive tool — you own the final work.
        <span className="text-primary underline-offset-2 hover:underline">Read more</span>
      </Link>
    );
  }

  if (variant === "inline") {
    return (
      <p
        className={cn(
          "mx-auto max-w-3xl text-center text-xs leading-relaxed text-muted-foreground",
          className,
        )}
      >
        <ShieldCheck className="mr-1 inline h-3.5 w-3.5 align-text-bottom text-primary" />
        {DISCLAIMER_FULL}{" "}
        <Link to="/terms" className="text-primary underline-offset-2 hover:underline">
          Read our Responsible AI Use & Terms
        </Link>
        .
      </p>
    );
  }

  if (variant === "footer") {
    return (
      <footer
        className={cn(
          "mt-16 border-t border-border bg-card/40 px-6 py-8 text-xs text-muted-foreground",
          className,
        )}
      >
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="max-w-2xl leading-relaxed">
            <ShieldCheck className="mr-1 inline h-3.5 w-3.5 align-text-bottom text-primary" />
            SmartEssay.ai is an assistive AI tool. You are responsible for editing, verifying
            sources, and following your institution's academic integrity policies.
          </p>
          <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <Link to="/terms" className="hover:text-foreground">Responsible AI Use</Link>
            <span aria-hidden>•</span>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <span aria-hidden>•</span>
            <Link to="/terms" hash="privacy" className="hover:text-foreground">Privacy</Link>
            <span aria-hidden>•</span>
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
          </nav>
        </div>
      </footer>
    );
  }

  // banner
  if (collapsible) {
    return (
      <div
        className={cn(
          "rounded-xl border border-primary/20 bg-primary/5 text-sm",
          className,
        )}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        >
          <span className="flex items-center gap-2 text-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span className="font-medium">Academic integrity notice</span>
            <span className="hidden text-muted-foreground sm:inline">
              — SmartEssay.ai is an assistive tool. Edit before submission.
            </span>
          </span>
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
        {open && (
          <div className="border-t border-primary/20 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
            {DISCLAIMER_FULL}{" "}
            <Link to="/terms" className="text-primary underline-offset-2 hover:underline">
              Read full Terms
            </Link>
            .
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="leading-relaxed text-muted-foreground">
          <div className="font-medium text-foreground">Academic integrity notice</div>
          <p className="mt-1 text-xs">{DISCLAIMER_FULL}</p>
          <Link
            to="/terms"
            className="mt-2 inline-block text-xs text-primary underline-offset-2 hover:underline"
          >
            Read full Terms →
          </Link>
        </div>
      </div>
    </div>
  );
}
